import type { Wording } from "./destructive-confirmation.ts"

/*
 * What each consequential, non-destructive action says before it goes ahead: making a Member an
 * Admin, archiving a Project or a Chat channel, moving an Epic's tickets to another Area with it,
 * moving a ticket out of the Member's Personal Project, where only they saw it, to a team Project,
 * taking over a change request its driver left, restoring a Document's Edit, archiving a
 * Folder or a Document, and a move that changes who reads one. None takes anything away, so none
 * is red, but each changes what other people can do or see, so each asks first. The list is
 * closed, as a Removal's is, and the wording lives here.
 */

export type Grant =
  | { confirms: "admin-grant"; member: string }
  /** `project` is the Project's key, OPS. */
  | { confirms: "archive-project"; project: string }
  /** `channel` is the Chat channel's name, without its `#`. */
  | { confirms: "archive-channel"; channel: string }
  /**
   * `epic` is the Epic's key, OPS-7; `area` the name of the Area it moves to; `moving` how many of
   * its tickets go with it.
   */
  | { confirms: "epic-area"; epic: string; area: string; moving: number }
  /**
   * `ticket` is the key of the Personal Project's ticket that moves, SM-3; `project` the key of
   * the team Project it moves to, or none until one is chosen.
   */
  | { confirms: "move-ticket"; ticket: string; project: string | null }
  /**
   * `changeRequest` is the change request taken over as its page names it, "OPS change request
   * 3"; `driver` the name of the driver who left it.
   */
  | { confirms: "take-over"; changeRequest: string; driver: string }
  /** `revision` is the number of the Document's Edit (the code's Revision) restored. */
  | { confirms: "restore-revision"; revision: number }
  /** `folder` is the name of the Folder archived, with everything beneath it. */
  | { confirms: "archive-folder"; folder: string }
  /** `document` is the title of the Document archived. */
  | { confirms: "archive-document"; document: string }
  /**
   * `moving` is the name of the Folder or title of the Document moved, `to` where it goes ("the
   * top level" for a Folder going there). What changes is listed in its `choice`.
   */
  | { confirms: "move-readers"; moving: string; to: string }

export function grantWordingOf(grant: Grant): Wording {
  switch (grant.confirms) {
    case "admin-grant":
      return {
        trigger: "Make Admin",
        menu: "Make Admin…",
        label: `Make ${grant.member} an Admin`,
        title: `Make ${grant.member} an Admin?`,
        subject: grant.member,
        consequence: "They can manage Members, Groups, the Agent and Spend.",
        confirm: "Make Admin",
      }
    case "archive-project":
      return {
        trigger: "Archive…",
        menu: "Archive Project…",
        label: `Archive ${grant.project}`,
        title: `Archive ${grant.project}?`,
        consequence:
          "Nobody can change it until it's unarchived. Everyone with access can still read it.",
        confirm: "Archive Project",
      }
    case "archive-channel":
      return {
        trigger: "Archive…",
        menu: "Archive channel…",
        label: `Archive #${grant.channel}`,
        title: `Archive #${grant.channel}?`,
        subject: `#${grant.channel}`,
        consequence:
          "Nobody can send messages, edit them or add people until it's unarchived. Everyone who can read it still can.",
        confirm: "Archive channel",
      }
    case "epic-area": {
      const tickets = grant.moving === 1 ? "ticket moves" : "tickets move"
      return {
        trigger: "Move Epic…",
        menu: "Move Epic…",
        label: `Move ${grant.epic} to ${grant.area}`,
        title: `Move ${grant.epic} to ${grant.area}?`,
        subject: grant.area,
        consequence: `Its ${grant.moving} ${tickets} to ${grant.area} with it.`,
        confirm: "Move Epic",
      }
    }
    case "move-ticket": {
      const where = grant.project ?? "a team Project"
      return {
        trigger: "Move to Project…",
        menu: "Move to Project…",
        label: `Move ${grant.ticket} to ${where}`,
        title: `Move ${grant.ticket} to ${where}?`,
        consequence: grant.project
          ? `Everyone who can read ${grant.project} will see it. It takes ${grant.project}'s ` +
            "next key, and its old link leads there."
          : "Everyone who can read that Project will see it. It takes the Project's next key, " +
            "and its old link leads there.",
        confirm: "Move ticket",
      }
    }
    case "take-over":
      return {
        trigger: "Take over…",
        menu: "Take over…",
        label: `Take over ${grant.changeRequest}`,
        title: `Take over ${grant.changeRequest}?`,
        consequence:
          `You become its driver: you answer its Questions and decide what it becomes. ` +
          `${grant.driver} is told in Slack.`,
        confirm: "Take over",
      }
    case "restore-revision":
      return {
        trigger: "Restore…",
        menu: "Restore Edit…",
        label: `Restore Edit ${grant.revision}`,
        title: `Restore Edit ${grant.revision}?`,
        consequence:
          "Its text and fields become the Draft, as a new Edit. Nothing is lost: " +
          "what the Document holds now stays in the Edit before it.",
        confirm: "Restore",
      }
    case "archive-folder":
      return {
        trigger: "Archive…",
        menu: "Archive Folder…",
        label: `Archive ${grant.folder}`,
        title: `Archive ${grant.folder}?`,
        subject: grant.folder,
        consequence:
          "Everything in it is archived with it: nobody can change it until it's restored. " +
          "Whoever reaches it finds it under Archived.",
        confirm: "Archive Folder",
      }
    case "archive-document":
      return {
        trigger: "Archive…",
        menu: "Archive Document…",
        label: `Archive ${grant.document}`,
        title: `Archive ${grant.document}?`,
        subject: grant.document,
        consequence:
          "Nobody can change it until it's restored. Whoever reaches it finds it under Archived.",
        confirm: "Archive Document",
      }
    case "move-readers":
      return {
        trigger: "Move…",
        menu: "Move…",
        label: `Move ${grant.moving} to ${grant.to}`,
        title: `Move ${grant.moving} to ${grant.to}?`,
        subject: grant.moving,
        consequence: "This changes who can read it, and everything in it:",
        confirm: "Move",
      }
  }
}
